import { useState, useEffect, useMemo } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { useTestSeries, useTestSeriesAssessments } from '../hooks'
import AssessmentCard from '../components/AssessmentCard'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

const TestSeriesView = () => {
  const navigate = useNavigate()
  const { seriesCode = '' } = useParams<{ seriesCode: string }>()

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('test_series', seriesCode, 'Test Series')
  }, [seriesCode])
  const [currentPage, setCurrentPage] = useState(0)
  const pageSize = 100

  // Fetch test series details
  const {
    data: testSeriesData,
    isLoading: isLoadingSeries,
    error: seriesError,
  } = useTestSeries(seriesCode)

  // Fetch assessments in the test series
  const {
    data: assessmentsData,
    isLoading: isLoadingAssessments,
    error: assessmentsError,
  } = useTestSeriesAssessments(seriesCode, currentPage, pageSize)

  // Sort assessments by availableFrom date (earliest first, null dates at the end)
  // Must be called before any early returns to satisfy Rules of Hooks
  const assessments = useMemo(() => {
    const content = assessmentsData?.content || []
    return [...content].sort((a, b) => {
      if (!a.availableFrom && !b.availableFrom) return 0
      if (!a.availableFrom) return 1
      if (!b.availableFrom) return -1
      return new Date(a.availableFrom).getTime() - new Date(b.availableFrom).getTime()
    })
  }, [assessmentsData?.content])

  const handleTakeTest = (testCode: string) => {
    navigate(`/mock-test/${testCode}/instructions`)
  }

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Loading state
  if (isLoadingSeries || isLoadingAssessments) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading test series...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (seriesError || assessmentsError) {
    const error = seriesError || assessmentsError
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl mx-auto">
          <div className="flex items-center mb-2">
            <span className="text-red-600 text-2xl mr-3">⚠️</span>
            <h2 className="text-xl font-semibold text-red-800">Error Loading Test Series</h2>
          </div>
          <p className="text-red-700 mb-4">
            {error instanceof Error ? error.message : 'Failed to load test series data'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  const testSeries = testSeriesData?.data
  const pageInfo = assessmentsData?.page

  return (
    <div>
      <Link
        to="/test-series"
        className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        All test series
      </Link>

      {/* Header Section */}
      {testSeries && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                {testSeries.seriesName}
              </h1>
              <p className="text-gray-600 text-sm sm:text-base">{testSeries.description}</p>
            </div>
            <div className="text-left sm:text-right">
              <div className="bg-blue-100 text-blue-800 px-3 py-2 rounded-lg inline-block">
                <p className="text-xs sm:text-sm font-medium">Year</p>
                <p className="text-xl sm:text-2xl font-bold">{testSeries.seriesYear}</p>
              </div>
            </div>
          </div>

          {/* Series Stats */}
          <div className="mt-4">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-3 sm:p-4 inline-block">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">Total Assessments</p>
              <p className="text-lg sm:text-xl font-semibold text-gray-900">
                {testSeries.assessmentCount}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Assessments Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            Assessments {pageInfo && `(${pageInfo.totalElements} total)`}
          </h2>
        </div>

        {/* Info Banner */}
        <div className="mb-4 sm:mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <p className="text-blue-800 text-xs sm:text-sm">
            <strong>Note:</strong> Below are all the assessments available in this test
            series. Click on "Take Test" to start an assessment.
          </p>
        </div>

        {/* Assessments Grid */}
        {assessments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {assessments.map((assessment) => (
              <AssessmentCard
                key={assessment.assessmentId}
                assessment={assessment}
                onTakeTest={handleTakeTest}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-8 sm:p-12 text-center">
            <p className="text-gray-500 text-base sm:text-lg">
              No assessments found in this test series
            </p>
          </div>
        )}

        {/* Pagination */}
        {pageInfo && pageInfo.totalPages > 1 && (
          <div className="mt-6 sm:mt-8 flex justify-center items-center space-x-3 sm:space-x-4">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pageInfo.hasPrevious}
              className="px-3 sm:px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Previous
            </button>
            <span className="text-gray-700 text-sm sm:text-base">
              Page {pageInfo.number + 1} of {pageInfo.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pageInfo.hasNext}
              className="px-3 sm:px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors text-sm sm:text-base"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TestSeriesView
